import React, { createContext, useContext, useEffect, useReducer } from 'react';
import im from 'immer';
import compareAsc from 'date-fns/compareAsc';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import parseISO from 'date-fns/parseISO';
import { PRESENTATION_CONTEXT, PRESENTATION_MODE, } from './types';
import equal from 'fast-deep-equal';
import { BeaconActions, BeaconControlData, BeaconEventData, BeaconEvents, ControlData, DatatrackDataTypes, PlayerEvents, useHostDataTrackMsg, useLocalDataTrack, usePlayerEvent, useVideoContext, } from '@alucio/video';
import useInterval from '../../../hooks/useInterval/useInterval';
import API, { graphqlOperation } from '@aws-amplify/api';
import { virtualUpdateSessionMeetingLog } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import { FileType, MeetingContentType } from '@alucio/aws-beacon-amplify/src/models';
const emptyContentState = {
    JWT: '',
    bucket: '',
    docPath: '',
    documentVersionId: '',
    documentId: '',
    contentType: FileType.PDF,
    visiblePages: [],
    state: {},
};
const initialState = {
    context: PRESENTATION_CONTEXT.APP,
    mode: PRESENTATION_MODE.NONE,
    lastUpdated: new Date('2000-01-01'),
    controlDataLog: [],
    content: emptyContentState,
    contentLog: [],
};
const PresentationStateContext = createContext({
    ...initialState,
    dispatch: () => { },
});
PresentationStateContext.displayName = 'PresentationStateContext';
export const usePresentationContext = () => useContext(PresentationStateContext);
const presentationStateReducer = (state, action) => {
    switch (action.type) {
        case 'presentContent': {
            const contentInfo = action.payload;
            if (!contentInfo) {
                throw (new Error('Content details must be provided when dispatching presentContent'));
            }
            if (state.context === PRESENTATION_CONTEXT.VIRTUAL_HOST) {
                if (state.content && state.mode === PRESENTATION_MODE.SHARE_CONTENT) {
                    // we are swapping docs also fire a stop sharing
                    analytics.track('DOCUMENT_STOP_PRESENT', {
                        action: 'STOP_PRESENT',
                        category: 'DOCUMENT',
                        context: 'VIRTUAL',
                        sessionId: state.virtualSesssionId,
                        documentId: state.content.documentId,
                        documentVersionId: state.content.documentVersionId,
                        customDeckId: state.content.customDeckId,
                    });
                }
                analytics.track('DOCUMENT_PRESENT', {
                    action: 'PRESENT',
                    category: 'DOCUMENT',
                    context: 'VIRTUAL',
                    documentId: contentInfo.documentId,
                    documentVersionId: contentInfo.documentVersionId,
                    sessionId: state.virtualSesssionId,
                    customDeckId: contentInfo.customDeckId,
                });
            }
            return im(state, draft => {
                draft.mode = PRESENTATION_MODE.SHARE_CONTENT;
                draft.lastUpdated = new Date();
                draft.content = {
                    ...contentInfo,
                };
                draft.controlDataLog.push(new BeaconControlData(BeaconActions.startPresentation, contentInfo.documentVersionId));
                if (state.context === PRESENTATION_CONTEXT.VIRTUAL_HOST &&
                    state.content && state.mode === PRESENTATION_MODE.SHARE_CONTENT) {
                    draft.contentLog[draft.contentLog.length - 1].endTime = new Date();
                }
                draft.contentLog.push({
                    customDeckId: contentInfo.customDeckId,
                    documentVersionId: contentInfo.documentVersionId,
                    startTime: new Date(),
                });
            });
        }
        case 'stopPresenting':
            return im(state, draft => {
                if (state.mode !== PRESENTATION_MODE.NONE) {
                    draft.mode = PRESENTATION_MODE.NONE;
                    draft.content = emptyContentState;
                    draft.lastUpdated = new Date();
                    draft.controlDataLog.push(new BeaconControlData(BeaconActions.stopPresentation));
                    if (state.mode === PRESENTATION_MODE.SHARE_CONTENT) {
                        // TODO move all analytics tracking here
                        if (state.content && state.context === PRESENTATION_CONTEXT.VIRTUAL_HOST) {
                            analytics.track('DOCUMENT_STOP_PRESENT', {
                                action: 'STOP_PRESENT',
                                category: 'DOCUMENT',
                                context: 'VIRTUAL',
                                sessionId: state.virtualSesssionId,
                                documentId: state.content.documentId,
                                documentVersionId: state.content.documentVersionId,
                                customDeckId: state.content.customDeckId,
                            });
                            draft.contentLog[draft.contentLog.length - 1].endTime = new Date();
                        }
                    }
                }
            });
        case 'startScreenShare':
            return im(state, draft => {
                if (state.mode !== PRESENTATION_MODE.SHARE_SCREEN) {
                    draft.mode = PRESENTATION_MODE.SHARE_SCREEN;
                    draft.content = emptyContentState;
                    draft.lastUpdated = new Date();
                    draft.controlDataLog.push(new BeaconControlData(BeaconActions.startScreenShare));
                }
            });
        case 'recordControlDataMessage':
            return im(state, draft => {
                draft.controlDataLog.push(action.payload);
            });
        case 'updateVirtualSessionId':
            return im(state, draft => {
                draft.virtualSesssionId = action.payload;
            });
        case 'updateContentState': {
            const contentState = action.payload;
            return im(state, draft => {
                if (state.content) {
                    const newContentState = {
                        ...state.content.state,
                        ...contentState,
                    };
                    if (draft.content && !equal(state.content.state, newContentState)) {
                        draft.content.state = newContentState;
                        draft.lastUpdated = new Date();
                    }
                }
            });
        }
        case 'updateFromMessage': {
            const message = action.payload;
            return im(state, draft => {
                if (compareAsc(state.lastUpdated, message.lastUpdated) === -1) {
                    if (state.mode !== message.mode) {
                        draft.mode = message.mode;
                        draft.content = message.content;
                    }
                    else if (message.content &&
                        state.content &&
                        (state.content.documentVersionId !== message.content.documentVersionId ||
                            state.content.customDeckId !== message.content.customDeckId ||
                            state.content.groupId !== message.content.groupId)) {
                        // Content has switched
                        draft.content = message.content;
                    }
                    else if (draft.content &&
                        state.content &&
                        message.content &&
                        !equal(state.content.state, message.content.state)) {
                        draft.content.state = message.content.state;
                    }
                    draft.lastUpdated = message.lastUpdated;
                }
            });
        }
        case 'resetMeetingLog':
            return im(state, draft => {
                draft.controlDataLog = [];
                draft.contentLog = [];
            });
        case 'saveMeetingLog':
            saveMeetingLog(state, action.payload);
            return state;
        default: return state;
    }
};
const saveMeetingLog = async (presentationState, saveComplete) => {
    const docHistoryMap = presentationState.contentLog.reduce((acc, value) => {
        let contentId = value.documentVersionId;
        let contentType = MeetingContentType.DOCUMENT_VERSION;
        if (value.customDeckId) {
            contentId = value.customDeckId;
            contentType = MeetingContentType.CUSTOM_DECK;
        }
        if (!contentId) {
            return acc;
        }
        if (!acc[contentId]) {
            acc[contentId] = {
                totalDuration: 0,
                contentType,
            };
        }
        const startTime = value.startTime;
        const endTime = value.endTime ? value.endTime : (new Date());
        acc[contentId].totalDuration += differenceInSeconds(endTime, startTime);
        return acc;
    }, {});
    const docHistory = Object.keys(docHistoryMap).map((contentId) => {
        return {
            contentId,
            totalDurationSec: docHistoryMap[contentId].totalDuration,
            contentType: docHistoryMap[contentId].contentType,
        };
    });
    const logEntries = presentationState.controlDataLog.reduce((acc, entry) => {
        // Need to JSON encode the param attribute also we only send controldata elements (not events or chat)
        if (entry instanceof ControlData) {
            const { param, ...rest } = entry;
            acc.push({
                param: param ? JSON.stringify(param) : null,
                ...rest,
            });
        }
        return acc;
    }, []);
    const params = {
        input: {
            sessionId: presentationState.virtualSesssionId,
            logEntries: logEntries,
            documentHistory: docHistory,
        },
    };
    await API.graphql(graphqlOperation(virtualUpdateSessionMeetingLog, params));
    if (saveComplete) {
        saveComplete();
    }
};
const handlePlayerEvent = (playerEvent, dispatch) => {
    if (playerEvent && playerEvent.data) {
        const data = playerEvent.data;
        if (data.event === PlayerEvents.statusEvent) {
            dispatch({
                type: 'updateContentState',
                payload: {
                    page: data.param.slide,
                    step: data.param.step,
                },
            });
        }
    }
};
export const PresentationContext = (props) => {
    const { children, context } = props;
    const [presentationState, dispatch] = useReducer(presentationStateReducer, { ...initialState, context });
    // Capture events from PPTX Viewer
    const playerEvent = usePlayerEvent();
    useEffect(() => {
        handlePlayerEvent(playerEvent, dispatch);
    }, [playerEvent]);
    return (<PresentationStateContext.Provider value={{ ...presentationState, dispatch }}>
      {children}
    </PresentationStateContext.Provider>);
};
export const VirtualPresentationContext = (props) => {
    const { isHost, children } = props;
    const state = {
        ...initialState,
        context: isHost ? PRESENTATION_CONTEXT.VIRTUAL_HOST : PRESENTATION_CONTEXT.VIRTUAL_ATTENDEE,
    };
    const [presentationState, dispatch] = useReducer(presentationStateReducer, state);
    const localDataTrack = useLocalDataTrack();
    const hostDataTrackMsg = useHostDataTrackMsg();
    const { room } = useVideoContext();
    // Host specific code
    // Capture events from PPTX Viewer
    const playerEvent = usePlayerEvent();
    useEffect(() => {
        if (isHost) {
            handlePlayerEvent(playerEvent, dispatch);
        }
    }, [playerEvent]);
    useEffect(() => {
        dispatch({
            type: 'updateVirtualSessionId',
            payload: room.sid,
        });
    }, [room.sid]);
    const sendMessage = () => {
        if (isHost) {
            // We only transmit a reduced version of the state (minus logs)
            const state = {
                mode: presentationState.mode,
                lastUpdated: presentationState.lastUpdated,
                content: presentationState.content,
            };
            const msg = new BeaconEventData(BeaconEvents.presentationHeartbeat, state);
            const jsonMsg = JSON.stringify(msg);
            localDataTrack && localDataTrack.send(jsonMsg);
        }
    };
    useInterval(sendMessage, 3000);
    useEffect(() => {
        sendMessage();
    }, [presentationState]);
    // Save meeting log whenever content changes
    useEffect(() => {
        if (isHost &&
            (presentationState.contentLog.length > 0 || presentationState.controlDataLog.length > 0)) {
            dispatch({
                type: 'saveMeetingLog',
                payload: undefined,
            });
        }
    }, [presentationState.content.documentVersionId, presentationState.content.customDeckId]);
    // Attendee specific code
    useEffect(() => {
        if (!isHost && hostDataTrackMsg) {
            const msgParsed = JSON.parse(hostDataTrackMsg);
            if (msgParsed.type === DatatrackDataTypes.BeaconEvent &&
                msgParsed.event === BeaconEvents.presentationHeartbeat) {
                // Need to do special handling for the tiemstamp field to force it into a date
                msgParsed.param.lastUpdated = parseISO(msgParsed.param.lastUpdated);
                if (!equal(msgParsed.param, presentationState)) {
                    dispatch({
                        type: 'updateFromMessage',
                        payload: msgParsed.param,
                    });
                }
            }
        }
    }, [hostDataTrackMsg]);
    return (<PresentationStateContext.Provider value={{ ...presentationState, dispatch }}>
      {children}
    </PresentationStateContext.Provider>);
};
//# sourceMappingURL=PresentationContext.js.map